import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* Dynamic viewport height for modern browsers */
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 100px); /* Bottom padding for mobile CTA access */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 100vh;
    min-height: 100dvh;
    min-height: -webkit-fill-available; /* Fallback for older iOS */
    padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Bottom padding on mobile for CTA access */
    overflow-y: scroll;
  }
  
  /* Extra small mobile devices */
  @media (max-width: 480px) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 140px);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: 0;
  padding-bottom: ${theme.spacing.lg}; /* Ensure content doesn't crowd the buttons */
  
  /* Ensure content can grow beyond viewport */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: auto;
    flex: none;
  }
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.lg};

  /* Support for HTML tags with higher specificity */
  & strong, & b {
    font-weight: ${theme.typography.weights.semiBold} !important;
    color: ${theme.colors.text} !important;
  }

  & em, & i {
    font-style: italic !important;
  }
`;

const SliderContainer = styled.div.attrs({
  className: 'slider-container'
})`
  width: 80vw !important;
  max-width: 80vw !important;
  margin: ${theme.spacing.md} auto;
  touch-action: pan-y;
  
  min-width: 80vw !important;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw !important;
    max-width: 80vw !important;
    min-width: 80vw !important;
  }
`;

const SliderTrack = styled.div.attrs({
  className: 'slider-track'
})`
  position: relative;
  height: 8px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 4px;
  margin: ${theme.spacing.sm} 0;
  touch-action: none;
`;

const SliderFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  border-radius: 4px;
  width: ${props => ((props.value - props.minValue) / (props.maxValue - props.minValue)) * 100}%;
  transition: width ${theme.animation.durations.normal};
`;

const SliderThumb = styled.div.attrs({
  className: 'slider-thumb'
})`
  position: absolute;
  top: 50%;
  left: ${props => ((props.value - props.minValue) / (props.maxValue - props.minValue)) * 100}%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  background: white;
  border: 4px solid ${theme.colors.primary};
  border-radius: 50%;
  cursor: pointer;
  box-shadow: ${theme.shadows.md};
  z-index: 10;
  transition: all ${theme.animation.durations.fast};
  touch-action: none;
  user-select: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    opacity: 0.3;
  }
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }
  
  &:active {
    transform: translate(-50%, -50%) scale(1.05);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 40px;
    height: 40px;
    
    &::after {
      width: 18px;
      height: 18px;
    }
  }
`;

const SliderInput = styled.input`
  width: 100%;
  height: 36px;
  background: transparent;
  outline: none;
  opacity: 0;
  position: absolute;
  top: -14px;
  left: 0;
  cursor: pointer;
  z-index: 20;
  touch-action: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 36px;
    height: 36px;
    background: white;
    border: 4px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    opacity: 1;
    box-shadow: ${theme.shadows.md};
  }

  &::-moz-range-thumb {
    width: 36px;
    height: 36px;
    background: white;
    border: 4px solid ${theme.colors.primary};
    cursor: pointer;
    box-shadow: ${theme.shadows.md};
  }
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const ValueDisplay = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin: ${theme.spacing.md} 0;
  flex: 0 0 auto;
`;

const CurrentValue = styled.span`
  font-family: ${theme.typography.headerFont};
  font-size: 2.5rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.primary};
`;

const ValueLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.textLight};
`;

const DebtDisplay = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const DebtTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const DebtAmount = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.error};
  margin: ${theme.spacing.md} 0;
`;

const DebtLabel = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

const VisualizationContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.xs} auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const VisualizationTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  letter-spacing: ${theme.typography.letterSpacing.tight};
`;

const DebtBurnChart = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 140px;
  background: ${theme.colors.surface};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.lg};
  position: relative;
  overflow: hidden;
  margin: ${theme.spacing.md} auto;
  padding: 8px 12px;
  box-sizing: border-box;
`;

const DailyBarsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  gap: 2px;
  padding: 30px 0 10px 0;
  position: relative;
`;

const XAxis = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  background: ${theme.colors.doryLighterBlue};
  z-index: 1;
`;

const DailyBar = styled.div`
  flex: 1;
  background: linear-gradient(135deg, ${theme.colors.error} 0%, ${theme.colors.warning} 100%);
  border-radius: 2px;
  min-width: 4px;
  transition: height ${theme.animation.durations.normal};
  position: relative;
  z-index: 2;
`;

const TimelineInfo = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  margin-top: ${theme.spacing.sm};
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Better width handling for side-by-side layout - doubled width */
  min-width: 240px;
  width: fit-content;
  max-width: 90%;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  /* Set margins as requested */
  margin-top: 0;
  margin-bottom: 24px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const PreviousButton = styled(ContinueButton)`
  /* Make the previous button much narrower */
  min-width: 60px;
  max-width: 80px;
  flex-shrink: 0;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-width: 70px;
    max-width: 90px;
    padding: 1rem 0.5rem; /* Less horizontal padding */
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-top: 0;
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
  flex-wrap: nowrap;
  position: relative;
  z-index: 10;
  
  /* When only one button, ensure it has side padding and doesn't go full width */
  ${ContinueButton}:only-child {
    max-width: 80%;
    min-width: 200px;
    
    /* On mobile, make it even more constrained */
    @media (max-width: ${theme.breakpoints.tablet}) {
      max-width: 75%;
      min-width: 180px;
    }
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-bottom: ${theme.spacing.xl};
  }
  
  /* Extra small mobile devices */
  @media (max-width: 480px) {
    margin-bottom: calc(${theme.spacing.xl} + 1rem);
  }
`;

const SafeSprintingScreen = ({ data, onNext, onSafeSprintingData, userProgress = {} }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [hasUserMovedSlider, setHasUserMovedSlider] = useState(false);
  const [sprintData, setSprintData] = useState({
    extraHoursPerDay: 10, // Start with 10 hours as default
    sprintDays: 8,
    repaymentDays: 5,
    recoveryHours: 1
  });

  // Get optimal pace from user progress
  const optimalPace = userProgress.calculatedMetrics?.optimalPace;
  const optimalHours = optimalPace?.optimalHours || 8; // Default to 8 hours if not available
  const optimalHoursFormatted = optimalPace?.optimalHoursFormatted || `${optimalHours} hour${optimalHours === 1 ? '' : 's'}`;

  // Calculate extra hours above optimal and productivity debt
  const extraHoursAboveOptimal = Math.max(0, sprintData.extraHoursPerDay - optimalHours);
  const productivityDebt = extraHoursAboveOptimal * sprintData.sprintDays;

  // Compute recovery period based on recovery hours below optimal pace
  const recoveryHoursPerDay = sprintData.recoveryHours;
  const recoveryDays = productivityDebt > 0 && recoveryHoursPerDay > 0
    ? Math.ceil(productivityDebt / recoveryHoursPerDay)
    : 0;

  // Helper function to convert decimal hours to spelled-out hours and minutes
  const formatHoursAndMinutes = (decimalHours) => {
    if (decimalHours <= 0) return 'zero hours';
    
    const wholeHours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - wholeHours) * 60);
    
    const hourNames = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'twenty-one', 'twenty-two', 'twenty-three', 'twenty-four'];
    const minuteNames = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'twenty-one', 'twenty-two', 'twenty-three', 'twenty-four', 'twenty-five', 'twenty-six', 'twenty-seven', 'twenty-eight', 'twenty-nine', 'thirty', 'thirty-one', 'thirty-two', 'thirty-three', 'thirty-four', 'thirty-five', 'thirty-six', 'thirty-seven', 'thirty-eight', 'thirty-nine', 'forty', 'forty-one', 'forty-two', 'forty-three', 'forty-four', 'forty-five', 'forty-six', 'forty-seven', 'forty-eight', 'forty-nine', 'fifty', 'fifty-one', 'fifty-two', 'fifty-three', 'fifty-four', 'fifty-five', 'fifty-six', 'fifty-seven', 'fifty-eight', 'fifty-nine'];
    
    if (wholeHours === 0) {
      return `${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (minutes === 0) {
      return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    } else {
      return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''} and ${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  // Calculate Safe Sprinting metrics
  const safeSprintingMetrics = {
    hoursNeededToWork: sprintData.extraHoursPerDay,
    daysOfSprint: `${sprintData.sprintDays} ${sprintData.sprintDays === 1 ? 'day' : 'days'}`,
    hoursBelowOptimalPace: sprintData.recoveryHours,
    adjustedOptimalPace: formatHoursAndMinutes(Math.max(0, optimalHours - sprintData.recoveryHours)),
    totalProductivityDebt: `${Math.round(productivityDebt)} ${Math.round(productivityDebt) === 1 ? 'hour' : 'hours'}`,
    daysToRecover: `${recoveryDays + 1} ${recoveryDays + 1 === 1 ? 'day' : 'days'}` // Add 1 to include the current day
  };

  // Debug: Log the calculated metrics
  console.log('SafeSprinting calculated metrics:', {
    sprintData,
    optimalHours,
    productivityDebt,
    recoveryDays,
    safeSprintingMetrics
  });
  
  // Debug: Log the formatted days value specifically
  console.log('DaysOfSprint formatted value:', safeSprintingMetrics.daysOfSprint);
  
  // Debug: Log the formatted productivity debt value specifically
  console.log('TotalProductivityDebt formatted value:', safeSprintingMetrics.totalProductivityDebt);
  
  // Debug: Log the formatted days to recover value specifically
  console.log('DaysToRecover formatted value:', safeSprintingMetrics.daysToRecover);
  
  // Debug: Log the formatted adjusted optimal pace value specifically
  console.log('AdjustedOptimalPace formatted value:', safeSprintingMetrics.adjustedOptimalPace);

  // Calculate repayment timeline bounds with safety checks
  const fastestRepayment = productivityDebt > 0 ? Math.max(1, Math.ceil(productivityDebt / optimalHours)) : 1;
  const slowestRepayment = productivityDebt > 0 ? Math.max(1, Math.ceil(productivityDebt / 1)) : 1; // 1 hour per day



  // Update recovery hours when optimal pace changes
  useEffect(() => {
    if (optimalHours > 0) {
      // Set recovery hours to the middle position by default
      const maxRecoveryHours = Math.max(2, Math.floor(optimalHours));
      const middleRecoveryHours = Math.round((1 + maxRecoveryHours) / 2);
      setSprintData(prev => ({ ...prev, recoveryHours: middleRecoveryHours }));
    }
  }, [optimalHours]);

  const handleNextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    try {
      // Debug: Log the calculated metrics
      console.log('SafeSprintingScreen: Calculated metrics:', safeSprintingMetrics);
      console.log('SafeSprintingScreen: Formatted values - Days:', safeSprintingMetrics.daysOfSprint, 'Debt:', safeSprintingMetrics.totalProductivityDebt, 'Recovery:', safeSprintingMetrics.daysToRecover, 'AdjustedPace:', safeSprintingMetrics.adjustedOptimalPace);
      
      // Use the callback to save Safe Sprinting metrics
      if (onSafeSprintingData) {
        await onSafeSprintingData(safeSprintingMetrics, data.key);
      } else {
        console.warn('onSafeSprintingData callback not provided, advancing to next screen');
        onNext?.(data.key);
      }
    } catch (error) {
      console.error('Error in handleFinish:', error);
      // Continue anyway if saving fails
      onNext?.(data.key);
    }
  };

  // Build daily debt values for bar visualization
  const dailyDebtValues = (() => {
    if (productivityDebt <= 0 || recoveryDays <= 0) return [];
    const days = recoveryDays;
    const values = [];
    for (let d = 0; d <= days; d += 1) {
      const remaining = Math.max(0, productivityDebt - recoveryHoursPerDay * d);
      values.push(remaining);
    }
    return values;
  })();

  const handleSliderChange = (field, value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      setSprintData(prev => ({ ...prev, [field]: numValue }));
      
      // Track that the user has moved the slider (specifically for extraHoursPerDay)
      if (field === 'extraHoursPerDay') {
        setHasUserMovedSlider(true);
      }
    }
  };

  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        

        {/* Step 1: Extra Hours Per Day */}
        {currentStep === 1 && (
          <>
            <Title>How many hours will you need to work each day during the sprint?</Title>
            <SliderContainer>
              <ScaleLabels>
                <span>1 hour</span>
                <span>24 hours</span>
              </ScaleLabels>
              <SliderTrack>
                <SliderFill value={sprintData.extraHoursPerDay} minValue={1} maxValue={24} />
                <SliderThumb value={sprintData.extraHoursPerDay} minValue={1} maxValue={24} />
                <SliderInput
                  type="range"
                  min="1"
                  max="24"
                  step="1"
                  value={sprintData.extraHoursPerDay}
                  onChange={(e) => handleSliderChange('extraHoursPerDay', e.target.value)}
                />
              </SliderTrack>
            </SliderContainer>

            <ValueDisplay style={{ marginBottom: theme.spacing.md, marginTop: 0 }}>
              <ValueLabel>You will work for</ValueLabel>
              <CurrentValue>{sprintData.extraHoursPerDay}</CurrentValue>
              <ValueLabel>{sprintData.extraHoursPerDay === 1 ? 'hour' : 'hours'} each day.</ValueLabel>
            </ValueDisplay>
            
            {(() => {
              // Don't show anything if user hasn't moved the slider yet
              if (!hasUserMovedSlider) {
                return null;
              }
              
              // If selected hours are below or equal to optimal pace
              if (sprintData.extraHoursPerDay <= optimalHours) {
                const selectedHours = sprintData.extraHoursPerDay;
                const hoursText = selectedHours === 1 ? '1 hour' : `${selectedHours} hours`;
                
                                 return (
                   <div style={{ 
                     textAlign: 'center', 
                     fontSize: theme.typography.sizes.body,
                     lineHeight: theme.typography.lineHeights.normal,
                     color: theme.colors.textLight,
                     marginTop: `calc(-${theme.spacing.md})`,
                     paddingTop: 0,
                     paddingLeft: theme.spacing.md,
                     paddingRight: theme.spacing.md,
                     paddingBottom: theme.spacing.lg
                   }}>
                     You're able to work <strong>{(() => {
                       if (selectedHours === 1) return 'one hour';
                       if (selectedHours === 2) return 'two hours';
                       if (selectedHours === 3) return 'three hours';
                       if (selectedHours === 4) return 'four hours';
                       if (selectedHours === 5) return 'five hours';
                       if (selectedHours === 6) return 'six hours';
                       if (selectedHours === 7) return 'seven hours';
                       if (selectedHours === 8) return 'eight hours';
                       if (selectedHours === 9) return 'nine hours';
                       if (selectedHours === 10) return 'ten hours';
                       if (selectedHours === 11) return 'eleven hours';
                       if (selectedHours === 12) return 'twelve hours';
                       if (selectedHours === 13) return 'thirteen hours';
                       if (selectedHours === 14) return 'fourteen hours';
                       if (selectedHours === 15) return 'fifteen hours';
                       if (selectedHours === 16) return 'sixteen hours';
                       if (selectedHours === 17) return 'seventeen hours';
                       if (selectedHours === 18) return 'eighteen hours';
                       if (selectedHours === 19) return 'nineteen hours';
                       if (selectedHours === 20) return 'twenty hours';
                       if (selectedHours === 21) return 'twenty-one hours';
                       if (selectedHours === 22) return 'twenty-two hours';
                       if (selectedHours === 23) return 'twenty-three hours';
                       if (selectedHours === 24) return 'twenty-four hours';
                       return `${selectedHours} hours`;
                     })()}</strong> per day without sprinting because your Optimal Pace is <strong>{optimalHoursFormatted}</strong>. Select more hours to begin a sprint.
                   </div>
                 );
              }
              
              // If selected hours are above optimal pace
              const extraHours = sprintData.extraHoursPerDay - optimalHours;
              let extraHoursText;
              
              // Number to word mapping for hours and minutes
              const numberToWords = {
                0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
                6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
                11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
                16: 'sixteen', 17: 'seventeen', 18: 'eighteen', 19: 'nineteen', 20: 'twenty',
                21: 'twenty-one', 22: 'twenty-two', 23: 'twenty-three', 24: 'twenty-four',
                25: 'twenty-five', 26: 'twenty-six', 27: 'twenty-seven', 28: 'twenty-eight',
                29: 'twenty-nine', 30: 'thirty', 31: 'thirty-one', 32: 'thirty-two',
                33: 'thirty-three', 34: 'thirty-four', 35: 'thirty-five', 36: 'thirty-six',
                37: 'thirty-seven', 38: 'thirty-eight', 39: 'thirty-nine', 40: 'forty',
                41: 'forty-one', 42: 'forty-two', 43: 'forty-three', 44: 'forty-four',
                45: 'forty-five', 46: 'forty-six', 47: 'forty-seven', 48: 'forty-eight',
                49: 'forty-nine', 50: 'fifty', 51: 'fifty-one', 52: 'fifty-two',
                53: 'fifty-three', 54: 'fifty-four', 55: 'fifty-five', 56: 'fifty-six',
                57: 'fifty-seven', 58: 'fifty-eight', 59: 'fifty-nine'
              };
              
              if (extraHours === 1) {
                extraHoursText = 'one hour';
              } else {
                const wholeHours = Math.floor(extraHours);
                const minutes = Math.round((extraHours - wholeHours) * 60);
                
                if (minutes === 0) {
                  const hourWord = numberToWords[wholeHours] || wholeHours;
                  extraHoursText = wholeHours === 1 ? 'one hour' : `${hourWord} hours`;
                } else if (wholeHours === 0) {
                  const minuteWord = numberToWords[minutes] || minutes;
                  extraHoursText = minutes === 1 ? 'one minute' : `${minuteWord} minutes`;
                } else {
                  const hourWord = numberToWords[wholeHours] || wholeHours;
                  const minuteWord = numberToWords[minutes] || minutes;
                  const hourText = wholeHours === 1 ? 'hour' : 'hours';
                  const minuteText = minutes === 1 ? 'minute' : 'minutes';
                  extraHoursText = `${hourWord} ${hourText} and ${minuteWord} ${minuteText}`;
                }
              }
              
              const selectedHoursText = sprintData.extraHoursPerDay === 1 ? '1 hour' : `${sprintData.extraHoursPerDay} hours`;
              
                             return (
                 <div style={{ 
                   textAlign: 'center', 
                   fontSize: theme.typography.sizes.body,
                   lineHeight: theme.typography.lineHeights.normal,
                   color: theme.colors.textLight,
                   marginTop: `calc(-${theme.spacing.md})`,
                   paddingTop: 0,
                   paddingLeft: theme.spacing.md,
                   paddingRight: theme.spacing.md,
                                        paddingBottom: theme.spacing.lg
                 }}>
                   That's <strong>{extraHoursText}</strong> more than your Optimal Pace (which is {optimalHoursFormatted}) every day. <strong>You're starting a sprint.</strong>
                 </div>
               );
            })()}
          </>
        )}

        {/* Step 2: Sprint Duration */}
        {currentStep === 2 && (
          <>
            <Title>How many days will you need to sprint?</Title>
            <SliderContainer>
              <ScaleLabels>
                <span>2 days</span>
                <span>14 days</span>
              </ScaleLabels>
              <SliderTrack>
                <SliderFill value={sprintData.sprintDays} minValue={2} maxValue={14} />
                <SliderThumb value={sprintData.sprintDays} minValue={2} maxValue={14} />
                <SliderInput
                  type="range"
                  min="2"
                  max="14"
                  step="1"
                  value={sprintData.sprintDays}
                  onChange={(e) => handleSliderChange('sprintDays', e.target.value)}
                />
              </SliderTrack>
            </SliderContainer>
            
            <ValueDisplay style={{ marginTop: 0, marginBottom: theme.spacing.lg }}>
            <ValueLabel>You will sprint for</ValueLabel>
              <CurrentValue>{sprintData.sprintDays}</CurrentValue>
              <ValueLabel>days.</ValueLabel>
            </ValueDisplay>
          </>
        )}

        {/* Step 3: Productivity Debt Calculation */}
        {currentStep === 3 && (
          <>
            
            <div style={{ 
              textAlign: 'center', 
              fontSize: theme.typography.sizes.bodyLarge,
              lineHeight: theme.typography.lineHeights.normal,
              color: theme.colors.text
            }}>
              Working {(() => {
                if (extraHoursAboveOptimal === 0) return 'zero extra hours';
                if (extraHoursAboveOptimal === 1) return 'one extra hour';
                
                const wholeHours = Math.floor(extraHoursAboveOptimal);
                const minutes = Math.round((extraHoursAboveOptimal - wholeHours) * 60);
                
                // Convert number to words
                const numberToWords = {
                  0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
                  6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
                  11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
                  16: 'sixteen', 17: 'seventeen', 18: 'eighteen', 19: 'nineteen', 20: 'twenty',
                  21: 'twenty-one', 22: 'twenty-two', 23: 'twenty-three', 24: 'twenty-four',
                  25: 'twenty-five', 26: 'twenty-six', 27: 'twenty-seven', 28: 'twenty-eight',
                  29: 'twenty-nine', 30: 'thirty', 31: 'thirty-one', 32: 'thirty-two',
                  33: 'thirty-three', 34: 'thirty-four', 35: 'thirty-five', 36: 'thirty-six',
                  37: 'thirty-seven', 38: 'thirty-eight', 39: 'thirty-nine', 40: 'forty',
                  41: 'forty-one', 42: 'forty-two', 43: 'forty-three', 44: 'forty-four',
                  45: 'forty-five', 46: 'forty-six', 47: 'forty-seven', 48: 'forty-eight',
                  49: 'forty-nine', 50: 'fifty', 51: 'fifty-one', 52: 'fifty-two',
                  53: 'fifty-three', 54: 'fifty-four', 55: 'fifty-five', 56: 'fifty-six',
                  57: 'fifty-seven', 58: 'fifty-eight', 59: 'fifty-nine', 60: 'sixty'
                };
                
                if (minutes === 0) {
                  const hourWord = wholeHours === 1 ? 'hour' : 'hours';
                  const hourText = numberToWords[wholeHours] || wholeHours;
                  return `an extra ${hourText} ${hourWord}`;
                } else if (wholeHours === 0) {
                  const minuteWord = minutes === 1 ? 'minute' : 'minutes';
                  const minuteText = numberToWords[minutes] || minutes;
                  return `an extra ${minuteText} ${minuteWord}`;
                } else {
                  const hourWord = wholeHours === 1 ? 'hour' : 'hours';
                  const minuteWord = minutes === 1 ? 'minute' : 'minutes';
                  const hourText = numberToWords[wholeHours] || wholeHours;
                  const minuteText = numberToWords[minutes] || minutes;
                  return `an extra ${hourText} ${hourWord} and ${minuteText} ${minuteWord}`;
                }
              })()} every day for {sprintData.sprintDays === 1 ? '1 day' : `${sprintData.sprintDays} days`} will create a <strong>total debt of {Math.round(productivityDebt)} hours</strong>.
            </div>

            {/* Debt Accumulation Visualization */}
            {productivityDebt > 0 && (
              <VisualizationContainer>
                <VisualizationTitle>Debt Accumulation During Your Sprint</VisualizationTitle>
                <DebtBurnChart>
                  <DailyBarsContainer>
                    {Array.from({ length: sprintData.sprintDays }, (_, index) => {
                      const dayNumber = index + 1;
                      const dayDebt = extraHoursAboveOptimal * dayNumber;
                      const maxHeight = extraHoursAboveOptimal * sprintData.sprintDays;
                      const heightPercentage = maxHeight > 0 ? (dayDebt / maxHeight) * 100 : 0;
                      
                      return (
                        <div key={index} style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flex: 1,
                          position: 'relative',
                          height: '100%'
                        }}>
                          <div
                            style={{
                              width: '80%',
                              height: `${Math.max(4, heightPercentage)}%`,
                              background: theme.colors.primary,
                              borderRadius: '2px',
                              position: 'relative',
                              minHeight: '4px'
                            }}
                          >
                            <div style={{
                              position: 'absolute',
                              top: '-20px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '10px',
                              color: theme.colors.textLight,
                              textAlign: 'center',
                              lineHeight: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              {Math.round(dayDebt)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </DailyBarsContainer>
                </DebtBurnChart>
              </VisualizationContainer>
            )}
            
            {/* Additional spacing below visualization */}
            <div style={{ height: '0.5px' }} />
          </>
        )}

        {/* Step 4: Recovery Hours Selection */}
        {currentStep === 4 && (
          <>
            <Title>How many hours per day can you afford to work below your Optimal Pace during the Recovery Period?</Title>
            
            {/* Recovery Hours Slider */}
            {productivityDebt > 0 ? (
              <>
                <SliderContainer>
                  <ScaleLabels>
                    <span>1 hour</span>
                    <span>{Math.max(2, Math.floor(optimalHours))} hours</span>
                  </ScaleLabels>
                  <SliderTrack>
                    <SliderFill value={sprintData.recoveryHours} minValue={1} maxValue={Math.max(2, Math.floor(optimalHours))} />
                    <SliderThumb value={sprintData.recoveryHours} minValue={1} maxValue={Math.max(2, Math.floor(optimalHours))} />
                    <SliderInput
                      type="range"
                      min="1"
                      max={Math.max(2, Math.floor(optimalHours))}
                      step="1"
                      value={sprintData.recoveryHours}
                      onChange={(e) => handleSliderChange('recoveryHours', e.target.value)}
                    />
                  </SliderTrack>
                </SliderContainer>
                
                <ValueDisplay style={{ marginTop: 0, marginBottom: 0 }}>
                  <ValueLabel>You will work</ValueLabel>
                  <CurrentValue>{sprintData.recoveryHours}</CurrentValue>
                  <ValueLabel>{sprintData.recoveryHours === 1 ? 'hour' : 'hours'} slower than your Optimal Pace.</ValueLabel>
                </ValueDisplay>
                
                {/* Recovery Information Text */}
                {productivityDebt > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    fontSize: theme.typography.sizes.bodyLarge,
                    lineHeight: theme.typography.lineHeights.normal,
                    color: theme.colors.text,
                    margin: `0 0 ${theme.spacing.xl} 0`,
                    padding: `0 ${theme.spacing.md}`
                  }}>
                    Your Optimal Pace will be reduced from <strong>{(() => {
                      const wholeHours = Math.floor(optimalHours);
                      const minutes = Math.round((optimalHours - wholeHours) * 60);
                      
                      // Helper function to convert numbers to words
                      const numberToWords = (num) => {
                        const words = {
                          0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
                          6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
                          11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
                          16: 'sixteen', 17: 'seventeen', 18: 'eighteen', 19: 'nineteen', 20: 'twenty',
                          21: 'twenty-one', 22: 'twenty-two', 23: 'twenty-three', 24: 'twenty-four',
                          25: 'twenty-five', 26: 'twenty-six', 27: 'twenty-seven', 28: 'twenty-eight',
                          29: 'twenty-nine', 30: 'thirty', 31: 'thirty-one', 32: 'thirty-two',
                          33: 'thirty-three', 34: 'thirty-four', 35: 'thirty-five', 36: 'thirty-six',
                          37: 'thirty-seven', 38: 'thirty-eight', 39: 'thirty-nine', 40: 'forty',
                          41: 'forty-one', 42: 'forty-two', 43: 'forty-three', 44: 'forty-four',
                          45: 'forty-five', 46: 'forty-six', 47: 'forty-seven', 48: 'forty-eight',
                          49: 'forty-nine', 50: 'fifty', 51: 'fifty-one', 52: 'fifty-two',
                          53: 'fifty-three', 54: 'fifty-four', 55: 'fifty-five', 56: 'fifty-six',
                          57: 'fifty-seven', 58: 'fifty-eight', 59: 'fifty-nine'
                        };
                        return words[num] || num;
                      };
                      
                      if (minutes === 0) {
                        if (wholeHours === 1) return 'one hour';
                        if (wholeHours === 2) return 'two hours';
                        if (wholeHours === 3) return 'three hours';
                        if (wholeHours === 4) return 'four hours';
                        if (wholeHours === 5) return 'five hours';
                        if (wholeHours === 6) return 'six hours';
                        if (wholeHours === 7) return 'seven hours';
                        if (wholeHours === 8) return 'eight hours';
                        if (wholeHours === 9) return 'nine hours';
                        if (wholeHours === 10) return 'ten hours';
                        return `${wholeHours} hours`;
                      } else if (wholeHours === 0) {
                        const minuteWord = minutes === 1 ? 'minute' : 'minutes';
                        return `${numberToWords(minutes)} ${minuteWord}`;
                      } else {
                        const hourWord = wholeHours === 1 ? 'hour' : 'hours';
                        const minuteWord = minutes === 1 ? 'minute' : 'minutes';
                        if (wholeHours === 1) return `one ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 2) return `two ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 3) return `three ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 4) return `four ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 5) return `five ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 6) return `six ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 7) return `seven ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 8) return `eight ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 9) return `nine ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 10) return `ten ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        return `${wholeHours} ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                      }
                    })()}</strong> to <strong>{(() => {
                      const adjustedPace = optimalHours - recoveryHoursPerDay;
                      const wholeHours = Math.floor(adjustedPace);
                      const minutes = Math.round((adjustedPace - wholeHours) * 60);
                      
                      if (adjustedPace <= 0) return 'zero hours';
                      
                      // Helper function to convert numbers to words
                      const numberToWords = (num) => {
                        const words = {
                          0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
                          6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
                          11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
                          16: 'sixteen', 17: 'seventeen', 18: 'eighteen', 19: 'nineteen', 20: 'twenty',
                          21: 'twenty-one', 22: 'twenty-two', 23: 'twenty-three', 24: 'twenty-four',
                          25: 'twenty-five', 26: 'twenty-six', 27: 'twenty-seven', 28: 'twenty-eight',
                          29: 'twenty-nine', 30: 'thirty', 31: 'thirty-one', 32: 'thirty-two',
                          33: 'thirty-three', 34: 'thirty-four', 35: 'thirty-five', 36: 'thirty-six',
                          37: 'thirty-seven', 38: 'thirty-eight', 39: 'thirty-nine', 40: 'forty',
                          41: 'forty-one', 42: 'forty-two', 43: 'forty-three', 44: 'forty-four',
                          45: 'forty-five', 46: 'forty-six', 47: 'forty-seven', 48: 'forty-eight',
                          49: 'forty-nine', 50: 'fifty', 51: 'fifty-one', 52: 'fifty-two',
                          53: 'fifty-three', 54: 'fifty-four', 55: 'fifty-five', 56: 'fifty-six',
                          57: 'fifty-seven', 58: 'fifty-eight', 59: 'fifty-nine'
                        };
                        return words[num] || num;
                      };
                      
                      if (minutes === 0) {
                        if (wholeHours === 1) return 'one hour';
                        if (wholeHours === 2) return 'two hours';
                        if (wholeHours === 3) return 'three hours';
                        if (wholeHours === 4) return 'four hours';
                        if (wholeHours === 5) return 'five hours';
                        if (wholeHours === 6) return 'six hours';
                        if (wholeHours === 7) return 'seven hours';
                        if (wholeHours === 8) return 'eight hours';
                        if (wholeHours === 9) return 'nine hours';
                        if (wholeHours === 10) return 'ten hours';
                        return `${wholeHours} hours`;
                      } else if (wholeHours === 0) {
                        const minuteWord = minutes === 1 ? 'minute' : 'minutes';
                        return `${numberToWords(minutes)} ${minuteWord}`;
                      } else {
                        const hourWord = wholeHours === 1 ? 'hour' : 'hours';
                        const minuteWord = minutes === 1 ? 'minute' : 'minutes';
                        if (wholeHours === 1) return `one ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 2) return `two ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 3) return `three ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 4) return `four ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 5) return `five ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 6) return `six ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 7) return `seven ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 8) return `eight ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 9) return `nine ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        if (wholeHours === 10) return `ten ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                        return `${wholeHours} ${hourWord} and ${numberToWords(minutes)} ${minuteWord}`;
                      }
                    })()}</strong> during the Recovery Period.
                  </div>
                )}
              </>
            ) : (
              <ValueDisplay>
                <CurrentValue>0</CurrentValue>
                <ValueLabel>hours below optimal pace (no debt to repay)</ValueLabel>
              </ValueDisplay>
            )}
          </>
        )}

        {/* Step 5: Repayment Timeline and Visualization */}
        {currentStep === 5 && (
          <>
            {/* Recovery Information Text */}
            {productivityDebt > 0 && (
              <>
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: theme.typography.sizes.bodyLarge,
                  lineHeight: theme.typography.lineHeights.normal,
                  color: theme.colors.text,
                  margin: `${theme.spacing.lg} 0`,
                  padding: `0 ${theme.spacing.md}`
                }}>
                  Working {(() => {
                    if (recoveryHoursPerDay === 1) return 'one hour';
                    if (recoveryHoursPerDay === 2) return 'two hours';
                    if (recoveryHoursPerDay === 3) return 'three hours';
                    if (recoveryHoursPerDay === 4) return 'four hours';
                    if (recoveryHoursPerDay === 5) return 'five hours';
                    if (recoveryHoursPerDay === 6) return 'six hours';
                    if (recoveryHoursPerDay === 7) return 'seven hours';
                    if (recoveryHoursPerDay === 8) return 'eight hours';
                    if (recoveryHoursPerDay === 9) return 'nine hours';
                    if (recoveryHoursPerDay === 10) return 'ten hours';
                    return `${recoveryHoursPerDay} hours`;
                  })()} below your Optimal Pace each day during the Recovery Period will allow you to <strong>fully recover in {recoveryDays + 1} days</strong>.
                </div>
                

              </>
            )}

            {/* Debt Burn Visualization */}
            <VisualizationContainer style={{ marginBottom: '24px' }}>
              <VisualizationTitle style={{ marginTop: '-24px' }}>Debt Repayment During Your Recovery</VisualizationTitle>
              {productivityDebt > 0 ? (
                <>
                  <DebtBurnChart>
                    <DailyBarsContainer>
                      {dailyDebtValues.map((debt, index) => {
                        const heightPercentage = productivityDebt > 0 ? (debt / productivityDebt) * 100 : 0;
                        
                        return (
                          <div key={index} style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flex: 1,
                            position: 'relative',
                            height: '100%'
                          }}>
                            <div
                              style={{
                                width: '80%',
                                height: `${Math.max(4, heightPercentage)}%`,
                                background: theme.colors.primary,
                                borderRadius: '2px',
                                position: 'relative',
                                minHeight: '4px'
                              }}
                            >
                              <div style={{
                                position: 'absolute',
                                top: '-20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: '10px',
                                color: theme.colors.textLight,
                                textAlign: 'center',
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                              }}>
                                {Math.round(debt)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </DailyBarsContainer>
                  </DebtBurnChart>
                </>
              ) : (
                <>
                  <DebtBurnChart>
                    <DailyBarsContainer>
                      {Array.from({ length: 10 }, (_, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flex: 1,
                          position: 'relative',
                          height: '100%'
                        }}>
                          <div
                            style={{
                              width: '80%',
                              height: '4px',
                              background: theme.colors.doryLighterBlue,
                              borderRadius: '2px',
                              position: 'relative',
                              minHeight: '4px'
                            }}
                          />
                        </div>
                      ))}
                    </DailyBarsContainer>
                  </DebtBurnChart>
                  <TimelineInfo>
                    Set your sprint parameters above to see your productivity debt calculation
                  </TimelineInfo>
                </>
              )}
            </VisualizationContainer>
          </>
        )}
        
        {/* CTA buttons appear right after content in a side-by-side layout */}
        <ButtonGroup>
          {currentStep > 1 && (
            <PreviousButton 
              onClick={handlePreviousStep}
              style={{ background: theme.colors.secondary, color: theme.colors.text }}
            >
              &lt;&lt;
            </PreviousButton>
          )}
          
          {currentStep < 5 ? (
            <ContinueButton 
              onClick={handleNextStep}
              disabled={currentStep === 1 && sprintData.extraHoursPerDay <= optimalHours}
              style={{
                ...(currentStep === 1 && sprintData.extraHoursPerDay <= optimalHours && {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                })
              }}
            >
              Next
            </ContinueButton>
          ) : (
            <ContinueButton onClick={handleFinish}>
              {data.content?.ctaText || 'Continue'}
            </ContinueButton>
          )}
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default SafeSprintingScreen;
